/**
 * App Store Connect – App Store localizations
 * @see https://developer.apple.com/help/app-store-connect/reference/app-information/app-store-localizations/
 * Metadata can be localized in these languages and locales.
 */
export const APP_STORE_LOCALIZATIONS: { value: string; label: string }[] = [
  { value: 'ar', label: 'Arabic' },
  { value: 'bn', label: 'Bangla' },
  { value: 'ca', label: 'Catalan' },
  { value: 'zh-Hans', label: 'Chinese (Simplified)' },
  { value: 'zh-Hant', label: 'Chinese (Traditional)' },
  { value: 'hr', label: 'Croatian' },
  { value: 'cs', label: 'Czech' },
  { value: 'da', label: 'Danish' },
  { value: 'nl', label: 'Dutch' },
  { value: 'en-AU', label: 'English (Australia)' },
  { value: 'en-CA', label: 'English (Canada)' },
  { value: 'en-GB', label: 'English (United Kingdom)' },
  { value: 'en-US', label: 'English (United States)' },
  { value: 'fi', label: 'Finnish' },
  { value: 'fr', label: 'French' },
  { value: 'fr-CA', label: 'French (Canada)' },
  { value: 'de', label: 'German' },
  { value: 'el', label: 'Greek' },
  { value: 'gu', label: 'Gujarati' },
  { value: 'he', label: 'Hebrew' },
  { value: 'hi', label: 'Hindi' },
  { value: 'hu', label: 'Hungarian' },
  { value: 'id', label: 'Indonesian' },
  { value: 'it', label: 'Italian' },
  { value: 'ja', label: 'Japanese' },
  { value: 'kn', label: 'Kannada' },
  { value: 'ko', label: 'Korean' },
  { value: 'ms', label: 'Malay' },
  { value: 'ml', label: 'Malayalam' },
  { value: 'mr', label: 'Marathi' },
  { value: 'no', label: 'Norwegian' },
  { value: 'or', label: 'Odia' },
  { value: 'pl', label: 'Polish' },
  { value: 'pt-BR', label: 'Portuguese (Brazil)' },
  { value: 'pt-PT', label: 'Portuguese (Portugal)' },
  { value: 'pa', label: 'Punjabi' },
  { value: 'ro', label: 'Romanian' },
  { value: 'ru', label: 'Russian' },
  { value: 'sk', label: 'Slovak' },
  { value: 'sl', label: 'Slovenian' },
  { value: 'es-MX', label: 'Spanish (Mexico)' },
  { value: 'es-ES', label: 'Spanish (Spain)' },
  { value: 'sv', label: 'Swedish' },
  { value: 'ta', label: 'Tamil' },
  { value: 'te', label: 'Telugu' },
  { value: 'th', label: 'Thai' },
  { value: 'tr', label: 'Turkish' },
  { value: 'uk', label: 'Ukrainian' },
  { value: 'ur', label: 'Urdu' },
  { value: 'vi', label: 'Vietnamese' },
];

/** Default locale when none exists in data (en-US per Apple) */
export const DEFAULT_APP_STORE_LOCALE = 'en-US';

/**
 * RevenueCat paywall’ların desteklediği locale’ler (App Store / UI kodlarıyla).
 * @see https://www.revenuecat.com/docs/tools/paywalls/creating-paywalls/localization
 * App Store’da olup RC’de olmayanlar (bn, gu, kn, …) burada yok.
 */
const REVENUECAT_ONLY_LOCALIZATIONS: { value: string; label: string }[] = [
  { value: 'az-AZ', label: 'Azerbaijani' },
  { value: 'eu', label: 'Basque' },
  { value: 'bg', label: 'Bulgarian' },
  { value: 'sr-Cyrl', label: 'Serbian (Cyrillic)' },
  { value: 'sr-Latn', label: 'Serbian (Latin)' },
  { value: 'es-419', label: 'Spanish (Latin America)' },
];

const REVENUECAT_SUPPORTED_FROM_APP_STORE = new Set([
  'ar',
  'ca',
  'zh-Hans',
  'zh-Hant',
  'hr',
  'cs',
  'da',
  'nl',
  'en-AU',
  'en-CA',
  'en-GB',
  'en-US',
  'fi',
  'fr',
  'fr-CA',
  'de',
  'el',
  'he',
  'hi',
  'hu',
  'id',
  'it',
  'ja',
  'ko',
  'ms',
  'no',
  'pl',
  'pt-BR',
  'pt-PT',
  'ro',
  'ru',
  'sk',
  'sl',
  'es-MX',
  'es-ES',
  'sv',
  'th',
  'tr',
  'uk',
  'vi',
]);

/** RevenueCat paywall locale picker / çeviri hedefleri için katalog. */
export const REVENUECAT_PAYWALL_LOCALIZATIONS: { value: string; label: string }[] = [
  ...APP_STORE_LOCALIZATIONS.filter(l => REVENUECAT_SUPPORTED_FROM_APP_STORE.has(l.value)),
  ...REVENUECAT_ONLY_LOCALIZATIONS,
].sort((a, b) => a.label.localeCompare(b.label, 'en'));

const REVENUECAT_PAYWALL_LOCALE_SET = new Set(REVENUECAT_PAYWALL_LOCALIZATIONS.map(l => l.value));

/** RC paywall için desteklenen locale mi (UI / App Store kodu). */
export function isRevenueCatPaywallLocale(locale: string): boolean {
  return REVENUECAT_PAYWALL_LOCALE_SET.has(locale);
}

/** Locale -> 2-letter country code for flag (özellikle hyphen'lı ve özel kodlar) */
const localeToCountry: Record<string, string> = {
  'zh-Hans': 'CN',
  'zh-Hant': 'TW',
  'en-US': 'US',
  'en-GB': 'GB',
  'en-AU': 'AU',
  'en-CA': 'CA',
  'pt-BR': 'BR',
  'pt-PT': 'PT',
  'fr-CA': 'CA',
  'es-MX': 'MX',
  'es-ES': 'ES',
  'es-419': 'MX',
  'az-AZ': 'AZ',
  'sr-Latn': 'RS',
  'sr-Cyrl': 'RS',
  ar: 'SA',
  bg: 'BG',
  bn: 'IN',
  eu: 'ES',
  gu: 'IN',
  he: 'IL',
  hi: 'IN',
  kn: 'IN',
  ml: 'IN',
  mr: 'IN',
  ms: 'MY',
  no: 'NO',
  or: 'IN',
  pa: 'IN',
  sl: 'SI',
  ta: 'IN',
  te: 'IN',
  uk: 'UA',
  ur: 'PK',
};

/**
 * Locale kodundan bayrak emoji döner (tüm localization listelerinde kullanılır).
 */
export function getLocaleFlag(locale: string): string {
  const country =
    localeToCountry[locale] ||
    locale.split('-').pop()?.toUpperCase().slice(0, 2) ||
    locale.toUpperCase().slice(0, 2);
  if (!country || country.length !== 2) return '';
  const a = country.charCodeAt(0);
  const b = country.charCodeAt(1);
  if (a < 65 || a > 90 || b < 65 || b > 90) return '';
  return String.fromCodePoint(0x1f1e6 + a - 65, 0x1f1e6 + b - 65);
}
